/* eslint-disable @angular-eslint/prefer-inject */
import { InventoryBundleDataService } from '../infrastructure/inventory-bundles/inventory-bundle.data.service';
import {
    InventoryBundleCreate,
    InventoryBundleDto,
    InventoryBundleRead,
    InventoryBundleSearchParams,
} from '../entities/inventory-bundle.dto';
import { InventoryBundleStore } from '../infrastructure/inventory-bundles/inventory-bundle.store';
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { InventoryBundleMapper } from '../infrastructure/inventory-bundles/inventory-bundle.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import { IQueryParams, SafeAny } from '@pf/shared-common';
import { WarehouseFacade } from './warehouse.facade';
import { map, Observable } from 'rxjs';
import {
    InventoryAdjustmentReason,
    PickStrategy,
    ResourceAuth,
    WmsPlatformEntity,
} from '@control-tower/platform-wms';

@Injectable()
export class InventoryBundleFacade extends AbstractManageEntityFacade<
    InventoryBundleDto,
    InventoryBundleRead,
    InventoryBundleCreate,
    InventoryBundleSearchParams
> {
    private readonly warehouseFacade = inject(WarehouseFacade);
    nameField: keyof InventoryBundleDto = 'name';

    constructor(
        dataService: InventoryBundleDataService,
        store: InventoryBundleStore,
        mapper: InventoryBundleMapper
    ) {
        super(dataService, null, store, mapper);
    }

    override getDefaultSearchParams$(
        params: IQueryParams<InventoryBundleDto>
    ): Observable<IQueryParams<InventoryBundleDto>> {
        return this.warehouseFacade.addDefaultWarehouseParam(params);
    }

    override applyResourceAuth(body: InventoryBundleCreate): void {
        const locationId = this.warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as SafeAny
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof InventoryBundleDto, string>> {
        return { name: searchText };
    }

    adjustInventoryForSourceEntity$(
        bundleId: string,
        adjustByQuantity: number,
        sourceEntity: WmsPlatformEntity,
        sourceEntityId: string
    ) {
        return (
            this.dataService as InventoryBundleDataService
        ).adjustInventory$({
            inventoryBundleId: bundleId,
            adjustByQuantity,
            sourceEntity,
            sourceEntityId,
            adjustmentReason: InventoryAdjustmentReason.Exception,
        });
    }

    adjustInventoryForException$(
        bundleId: string,
        adjustByQuantity: number,
        exceptionId: string,
        reason: InventoryAdjustmentReason = InventoryAdjustmentReason.Exception
    ) {
        return (
            this.dataService as InventoryBundleDataService
        ).adjustInventory$({
            inventoryBundleId: bundleId,
            adjustByQuantity,
            exceptionId,
            adjustmentReason: reason,
        });
    }

    partialMoveForException$(
        bundleId: string,
        destinationContainerId: string,
        quantityToMove: number,
        pickStrategy: PickStrategy,
        exceptionId: string
    ) {
        return (this.dataService as InventoryBundleDataService).partialMove$(
            bundleId,
            {
                containerId: destinationContainerId,
                quantity: quantityToMove,
                pickStrategy,
                exceptionId,
                adjustmentReason: InventoryAdjustmentReason.Exception,
            }
        );
    }

    withdrawInventoryForException$(
        bundleId: string,
        quantityToWithdraw: number,
        pickStrategy: PickStrategy,
        exceptionId: string
    ) {
        return (
            this.dataService as InventoryBundleDataService
        ).withdrawInventory$(bundleId, {
            withdrawQuantity: quantityToWithdraw,
            pickStrategy,
            exceptionId,
            adjustmentReason: InventoryAdjustmentReason.Exception,
        });
    }

    getBundleByItemIdAndSlotId$(itemId: string, slotId: string) {
        return this.oneTimeSearch$({
            pageSize: 1,
            pageIndex: 1,
            filters: {
                item: itemId,
                slotId,
            } as SafeAny,
        }).pipe(
            map(result => {
                return result.data.length > 0 ? result.data[0] : null;
            })
        );
    }
}
